<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useBreakpointsForElement } from '@tools/layout';

type GapSize = 0 | 1 | 2 | 4 | 'responsive';
type GapSizeItem = Array<string | Record<string, boolean>>;

const props = withDefaults(
  defineProps<{
    addBottomSpacing?: boolean;
    addTopSpacing?: boolean;
    gap?: GapSize;
    showDivider?: boolean;
  }>(),
  {
    gap: 4,
  },
);

const root = useTemplateRef<HTMLDivElement>('root');

const { min2xl } = useBreakpointsForElement(root, { size: '2xl', type: 'min' });

const gapsizes = computed<Record<GapSize, GapSizeItem>>(() => ({
  0: [],
  1: [
    '*:py-1',
    {
      'pb-1': props.addBottomSpacing,
      'pt-1': props.addTopSpacing,
    },
  ],
  2: [
    '*:py-2',
    {
      'pb-2': props.addBottomSpacing,
      'pt-2': props.addTopSpacing,
    },
  ],
  4: [
    '*:py-4',
    {
      'pb-4': props.addBottomSpacing,
      'pt-4': props.addTopSpacing,
    },
  ],
  responsive: [
    '*:py-6',
    {
      '*:py-8': min2xl.value,
      'pb-6 pb-8': props.addBottomSpacing && min2xl.value,
      'pt-6 pt-8': props.addTopSpacing && min2xl.value,
    },
  ],
}));
</script>

<template>
  <div class="flex flex-col" ref="root">
    <div
      :class="[
        '*:first:pt-0 *:last:pb-0',
        ...gapsizes[props.gap],
        {
          'divide-x-0 divide-y divide-solid divide-grey-200 *:border-grey-200 not-first:*:first:border-t not-last:*:last:border-b':
            props.showDivider,
        },
      ]"
    >
      <slot></slot>
    </div>
  </div>
</template>
